import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { gql } from 'apollo-angular';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';
import { UserWithRoleInterface } from '../components/popups/share-study/components/share-by-email/share-by-email.component';
import type { AlbumAddOrUpdateInterface } from '../models/album-add.interface';
import { AlbumTypeEnum } from '../models/album-add.interface';
import type { AlbumWithCaseInterface } from '../models/album-case.interface';
import type { AlbumInterface } from '../models/album.interface';
import type { SharedEntityInterface } from '../models/shared-study.interface';
import type { GetEntitySharesQueryInterface } from './study-share.service';
import { GetEntitySharesQuery, GetEntitySharesQueryVariables } from './study-share.service';

export const GetAlbumsQuery = gql`query getAlbums($clinicId : Long!, $search: String) {
  albums(clinicId :$clinicId, where: { name: { startsWith: $search } })
	{
		id
		createdAt
		type
		name
    userId
		userName
    nbCases
    clinicId
    clinicName
    shareUserIds
    {
      userId
      clinicSource
    }
    shareRoles
    {
      roleId
      clinicSource
    }
	}
}`;

export const GetAlbumsWithCasesQuery = gql`query getAlbumsWithCases($clinicId : Long!, $search: String) {
  albums(clinicId :$clinicId, where: { cases: { some: { name: { startsWith: $search } } } })
	{
		id
		createdAt
		type
		name
		userName
    nbCases
    clinicId
    cases {
      id
      name
      createdAt
      type
      study {
        id
        publicId
        name
        previewHash
        previewPublicId
      }
    }
	}
}`;
export let GetAlbumsQueryVariables: any = null;

@Injectable({
  providedIn: 'root'
})
export class AlbumService {
  constructor(private readonly apollo: Apollo) { }

  getAlbums(clinicId: number, search?: string): Observable<AlbumInterface[]> {
    GetAlbumsQueryVariables = {
      search: search ?? '',
      clinicId
    };
    return this.apollo.query<{
      albums: AlbumInterface[];
    }>({
      query: GetAlbumsQuery,
      variables: GetAlbumsQueryVariables
    }).pipe(map(res => res.data.albums));
  }

  getAlbumsWithCases(clinicId: number, search?: string): Observable<AlbumWithCaseInterface[]> {
    GetAlbumsQueryVariables = {
      search: search ?? '',
      clinicId
    };
    return this.apollo.query<{
      albums: AlbumWithCaseInterface[];
    }>({
      query: GetAlbumsWithCasesQuery,
      variables: GetAlbumsQueryVariables
    }).pipe(map(res => res.data.albums));
  }

  addOrUpdateAlbum(albumAddOrUpdate: AlbumAddOrUpdateInterface): Observable<AlbumInterface | undefined> {
    return this.apollo.mutate<{
      addAlbum: AlbumInterface;
    }>({
      mutation: gql`mutation addAlbum($dto : AlbumAddDtoInput)
            {
                addAlbum( dto : $dto)
                {
                  id
                  createdAt
                  type
                  name
                  userName
                  nbCases
                  clinicId
                }
            }`,
      variables:
      {
        dto:
        {
          type: AlbumTypeEnum[albumAddOrUpdate.type].toUpperCase(),
          name: albumAddOrUpdate.name,
          id: albumAddOrUpdate.id
        }
      },
      update: (cache, { data }) => {
        // Evict all cached entries for the `albums` and `albumsWithCases` root fields,
        // regardless of their (clinicId, search) variable tuple. The previous manual
        // readQuery/writeQuery flow above used incomplete variables ({clinicId} only,
        // missing `search`) so Apollo returned null for the cache lookup and skipped
        // the update — the new album never appeared until a hard reload.
        // cache.gc() then drops the now-unreachable normalized entities.
        if (data?.addAlbum) {
          cache.evict({ fieldName: 'albums' });
          cache.evict({ fieldName: 'albumsWithCases' });
          cache.gc();
        }
      },
      // Refetch active observers by operation name (variable-agnostic) so any
      // currently-displayed album list updates immediately.
      refetchQueries: ['getAlbums', 'getAlbumsWithCases']
    }

    ).pipe(map(res => res.data ? res.data?.addAlbum : undefined));
  }

  updateAlbum(albumAddOrUpdate: AlbumAddOrUpdateInterface): Observable<AlbumInterface | undefined> {
    return this.apollo.mutate<{
      updateAlbum: AlbumInterface;
    }>({
      mutation: gql`mutation updateAlbum($dto : AlbumAddDtoInput)
          {
              updateAlbum( dto : $dto)
              {
                id
                createdAt
                type
                name
                userName
                nbCases
                clinicId
              }
          }`,
      variables:
      {
        dto:
        {
          type: AlbumTypeEnum[albumAddOrUpdate.type].toUpperCase(),
          name: albumAddOrUpdate.name,
          id: albumAddOrUpdate.id
        }
      },
      update: (cache, { data }) => {
        // See addOrUpdateAlbum for rationale.
        if (data?.updateAlbum) {
          cache.evict({ fieldName: 'albums' });
          cache.evict({ fieldName: 'albumsWithCases' });
          cache.gc();
        }
      },
      refetchQueries: ['getAlbums', 'getAlbumsWithCases']
    }

    ).pipe(map(res => res.data ? res.data?.updateAlbum : undefined));
  }

  deleteAlbum(id: number): Observable<AlbumInterface | undefined> {
    return this.apollo.mutate<{
      deleteAlbum: AlbumInterface;
    }>({
      mutation: gql`mutation deleteAlbum($id : Long!)
          {
              deleteAlbum(id : $id)
              {
                id
                createdAt
                type
                name
                userName
                clinicId
              }
          }`,
      variables:
      {
        id
      },
      update: (cache, { data }) => {
        // See addOrUpdateAlbum for rationale.
        if (data?.deleteAlbum) {
          cache.evict({ fieldName: 'albums' });
          cache.evict({ fieldName: 'albumsWithCases' });
          cache.gc();
        }
      },
      refetchQueries: ['getAlbums', 'getAlbumsWithCases']
    }).pipe(map(res => res.data ? res.data?.deleteAlbum : undefined));
  }

  public shareAlbumToUsersByWeb(albumId: number, userIds: number[], clinicIds: number[], shareWithMyGroup: boolean): Observable<ShareAlbumToUsersByWebResultDto | undefined> {
    return this.apollo.mutate<ShareStudyToUsersByWebResponse>({
      mutation : gql`mutation shareAlbumToUsersByWeb($albumId: Long!, $userIds: [Long!], $clinicIds: [Long!], $shareWithMyGroup: Boolean!) {
        shareAlbumToUsersByWeb(albumId: $albumId, userIds: $userIds, clinicIds: $clinicIds, shareWithMyGroup: $shareWithMyGroup) {
          album
          {
            id
            name
          }
          shares
          {
            id
            targetName,
            shareTargetType
            targetId
          }
        }
      }`,
      variables: {
        albumId,
        userIds,
        clinicIds,
        shareWithMyGroup
      },
      update: (store, { data }) => {

        if (data?.shareAlbumToUsersByWeb) {

          // Read the data from our cache for this query.
          const getEntitySharesQuery = store.readQuery<GetEntitySharesQueryInterface>({ query: GetEntitySharesQuery, variables: GetEntitySharesQueryVariables });
          if (getEntitySharesQuery) {
            let shares = getEntitySharesQuery.sharedEntities.map(shares => Object.assign({}, shares));
            data?.shareAlbumToUsersByWeb.shares.forEach(share => {
              if (!shares.map(k => k.id).includes(share.id)) {
                shares.push(share);
              }
            });

            shares = shares.sort((a, b) => a.targetName.localeCompare(b.targetName));

            store.writeQuery<GetEntitySharesQueryInterface>({ query: GetEntitySharesQuery, data: { sharedEntities :shares }, variables: GetEntitySharesQueryVariables });
          }
        }
      },
    }).pipe(map(res =>res.data ? res.data.shareAlbumToUsersByWeb : undefined));
  }

  public shareAlbumToUsersByEmail(albumId: number, newUsers: UserWithRoleInterface[], userIds: number[], locale: string  ): Observable<AlbumInterface | undefined> {
    return this.apollo.mutate<ShareStudyByEmailResponse>({
      mutation : gql`mutation shareAlbumByEmail($dto: ShareStudyByEmailInputDtoInput!) {
        shareStudyByEmail(dto: $dto) {
          __typename
        }
      }`,
      variables: {
        dto: {
          albumId,
          userIds,
          newUsers,
          locale,
        }
      },
    }).pipe(map(res =>res.data ? res.data.shareAlbumByEmail : undefined));
  }
}

export interface GetAlbumsQueryInterface {
  albums: AlbumInterface[];
}

export interface ShareStudyToUsersByWebResponse {
  shareAlbumToUsersByWeb: ShareAlbumToUsersByWebResultDto;
}

export interface ShareStudyByEmailResponse {
  shareAlbumByEmail: AlbumInterface;
}

export interface ShareAlbumToUsersByWebResultDto {
  album: AlbumInterface;
  shares: SharedEntityInterface[];
}
